/**
 * Zugriff auf die API.
 *
 * Die Anmeldung läuft über ein HttpOnly-Cookie, das der Browser von selbst
 * mitschickt -- hier gibt es deshalb keinen Token zu verwalten. `credentials`
 * muss trotzdem gesetzt sein, sonst lässt fetch das Cookie weg, sobald
 * Oberfläche und Backend im Entwicklungsbetrieb auf verschiedenen Ports
 * liegen.
 */

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }

  /** Nicht angemeldet -- die Oberfläche schickt dann zur Anmeldung. */
  get unauthorized(): boolean {
    return this.status === 401
  }

  /**
   * Es lag am Netz, nicht an der Anfrage.
   *
   * Status 0 gibt es in keiner HTTP-Antwort; er steht hier für „gar keine
   * Antwort". Daran hängt eine Entscheidung, die man nicht raten darf: was am
   * Netz scheitert, wartet im Ausgangskorb auf später — was der Server
   * ablehnt, wartet nie, sondern gehört dem Menschen gesagt.
   */
  get offline(): boolean {
    return this.status === 0
  }
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '')

async function request<T>(
  path: string,
  init: RequestInit & { query?: Record<string, string | number | undefined> } = {},
): Promise<T> {
  const { query, ...rest } = init
  let url = `${BASE}${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, String(value))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }

  // Ohne Netz wirft `fetch` einen TypeError, dessen Text vom Browser kommt
  // („Load failed" in Safari, „Failed to fetch" in Chrome). Der stünde sonst
  // wörtlich in der Oberfläche -- und ließe sich nirgends von einer Absage des
  // Servers unterscheiden.
  let response: Response
  try {
    response = await fetch(url, {
      credentials: 'include',
      headers:
        rest.body === undefined ? undefined : { 'Content-Type': 'application/json' },
      ...rest,
    })
  } catch {
    throw new ApiError(0, 'Keine Verbindung zum Server.')
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readError(response))
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

async function readError(response: Response): Promise<string> {
  return errorText(await response.text().catch(() => ''), response.status)
}

function errorText(body: string, status: number): string {
  try {
    const data = JSON.parse(body)
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
    // Validierungsfehler von FastAPI kommen als Liste; die erste Meldung
    // reicht, mehr als eine hilft dem Nutzer ohnehin nicht weiter.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string }
      if (first?.msg) return first.msg
    }
  } catch {
    // Antwort ohne JSON -- dann bleibt es beim allgemeinen Text unten.
  }
  if (status >= 500) return 'Der Server hat einen Fehler gemeldet.'
  return 'Das hat nicht geklappt.'
}

/**
 * Ein Bild hochladen und dabei sagen, wie weit es ist.
 *
 * Hier steht als einzige Stelle der Anwendung `XMLHttpRequest` statt `fetch`:
 * `fetch` meldet keinen Fortschritt beim Senden. Ein Rumpf als Datenstrom
 * wäre der moderne Weg, aber Safari kennt ihn nicht -- und Safari ist auf
 * dieser Reise das Hauptgerät. Unterwegs am schmalen Netz ist der Balken
 * nicht Zierde: ohne ihn sieht ein Upload von zwanzig Sekunden aus wie ein
 * Absturz.
 */
function uploadRequest<T>(
  path: string,
  blob: Blob,
  onProgress?: (anteil: number) => void,
  method: 'POST' | 'PATCH' = 'POST',
  headers: Record<string, string> = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, `${BASE}${path}`)
    xhr.withCredentials = true
    xhr.setRequestHeader('Content-Type', blob.type || 'application/octet-stream')
    for (const [name, wert] of Object.entries(headers)) {
      xhr.setRequestHeader(name, wert)
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total)
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Der Server hat es; der Balken darf voll sein, auch wenn das
        // letzte Fortschrittsereignis ausgeblieben ist.
        onProgress?.(1)
        try {
          resolve(JSON.parse(xhr.responseText) as T)
        } catch {
          reject(new ApiError(xhr.status, 'Die Antwort des Servers war unlesbar.'))
        }
        return
      }
      reject(new ApiError(xhr.status, errorText(xhr.responseText, xhr.status)))
    }

    xhr.onerror = () => reject(new ApiError(0, 'Keine Verbindung zum Server.'))
    xhr.onabort = () => reject(new ApiError(0, 'Das Hochladen wurde abgebrochen.'))

    xhr.send(blob)
  })
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | number | undefined>) =>
    request<T>(path, { method: 'GET', query }),

  post: <T>(path: string, body?: unknown, query?: Record<string, string | number | undefined>) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? '{}' : JSON.stringify(body),
      query,
    }),

  /**
   * Ein Bild -- roh im Rumpf, kein multipart.
   *
   * Der Server erkennt den Typ am Inhalt, nicht am Content-Type; die Angabe
   * hier ist nur Höflichkeit. Ohne multipart-Rahmen ist die Anfrage außerdem
   * so groß wie die Datei und nicht größer, was auf einem Webhosting-Paket
   * mit knappem `post_max_size` zählt.
   */
  upload: <T>(path: string, blob: Blob, onProgress?: (anteil: number) => void) =>
    uploadRequest<T>(path, blob, onProgress),

  /**
   * Ein Stück eines Videos.
   *
   * `Content-Range` sagt, wo das Stück hingehört. Der Server prüft es gegen
   * seinen Stand und lehnt ab, was nicht passt — das ist der Unterschied
   * zwischen „weitermachen" und „Datei kaputtschreiben".
   */
  uploadChunk: <T>(
    path: string,
    blob: Blob,
    range: string,
    onProgress?: (anteil: number) => void,
  ) => uploadRequest<T>(path, blob, onProgress, 'PATCH', { 'Content-Range': range }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? '{}' : JSON.stringify(body),
    }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
